import React, { useState } from 'react';
import { Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { 
    FaTrash, 
    FaCheck, 
    FaCheckDouble, 
    FaSync, 
    FaClock, 
    FaExclamationTriangle,
    FaInfoCircle,
    FaUser,
    FaPills,
    FaCalendarAlt,
    FaFileAlt,
    FaBell,
    FaShieldAlt
} from 'react-icons/fa';
import moment from 'moment';
import './NotificationPanel.css';

const NotificationPanel = ({
    notifications = [],
    unreadCount = 0,
    isLoading = false,
    isConnected = true,
    onMarkAsRead,
    onMarkAllAsRead,
    onDeleteNotification,
    onRefresh
}) => {
    const [selectedNotifications, setSelectedNotifications] = useState(new Set());
    const [showAllNotifications, setShowAllNotifications] = useState(false);

    const getNotificationIcon = (notificationType, priority) => {
        const iconClass = `notification-icon ${priority?.toLowerCase() || 'normal'}`;
        
        const iconMap = {
            'PATIENT_REGISTRATION': <FaUser className={iconClass} />,
            'PATIENT_ASSIGNMENT': <FaUser className={iconClass} />,
            'MEDICATION_REMINDER': <FaPills className={iconClass} />,
            'APPOINTMENT_REMINDER': <FaCalendarAlt className={iconClass} />,
            'APPOINTMENT_SCHEDULED': <FaCalendarAlt className={iconClass} />,
            'FORM_SUBMITTED': <FaFileAlt className={iconClass} />,
            'FORM_APPROVED': <FaFileAlt className={iconClass} />,
            'SAFETY_ALERT': <FaShieldAlt className={iconClass} />,
            'DRUG_INTERACTION_ALERT': <FaExclamationTriangle className={iconClass} />,
            'ALLERGY_ALERT': <FaExclamationTriangle className={iconClass} />,
            'CRITICAL_LAB_RESULT': <FaExclamationTriangle className={iconClass} />,
            'LAB_RESULT_READY': <FaInfoCircle className={iconClass} />
        };

        return iconMap[notificationType] || <FaBell className={iconClass} />;
    };

    const getPriorityBadge = (priority) => {
        const priorityConfig = {
            'CRITICAL': { variant: 'danger', text: 'Critical' },
            'URGENT': { variant: 'warning', text: 'Urgent' },
            'HIGH': { variant: 'info', text: 'High' },
            'NORMAL': { variant: 'secondary', text: 'Normal' },
            'LOW': { variant: 'light', text: 'Low' }
        };

        const config = priorityConfig[priority] || priorityConfig['NORMAL'];
        return <Badge bg={config.variant} className="priority-badge">{config.text}</Badge>;
    };

    const getTimeAgo = (dateString) => {
        return moment(dateString).fromNow();
    };

    const isUnread = (notification) => {
        return notification.status !== 'read' && notification.status !== 'acknowledged';
    };

    const handleNotificationClick = (notification) => {
        if (isUnread(notification)) {
            onMarkAsRead(notification.notification_id);
        }
    };

    const toggleNotificationSelection = (notificationId) => {
        const newSelected = new Set(selectedNotifications);
        if (newSelected.has(notificationId)) {
            newSelected.delete(notificationId);
        } else {
            newSelected.add(notificationId);
        }
        setSelectedNotifications(newSelected);
    };

    const handleBulkDelete = () => {
        selectedNotifications.forEach(notificationId => {
            onDeleteNotification(notificationId);
        });
        setSelectedNotifications(new Set());
    };

    const displayedNotifications = showAllNotifications ? notifications : notifications.slice(0, 10);

    if (isLoading) {
        return (
            <div className="notification-panel-loading">
                <Spinner animation="border" size="sm" />
                <span className="ms-2">Loading notifications...</span>
            </div>
        );
    }

    return (
        <div className="notification-panel">
            {/* Header */}
            <div className="notification-panel-header">
                <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                        Notifications 
                        {unreadCount > 0 && (
                            <Badge bg="danger" className="ms-2">{unreadCount}</Badge>
                        )}
                    </h6>
                    <div className="d-flex gap-2">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={onRefresh}
                            disabled={isLoading}
                            title="Refresh notifications"
                        >
                            <FaSync />
                        </Button>
                        {unreadCount > 0 && (
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={onMarkAllAsRead}
                                title="Mark all as read"
                            >
                                <FaCheckDouble />
                            </Button>
                        )}
                    </div>
                </div>
                
                {!isConnected && (
                    <Alert variant="warning" className="mt-2 mb-0 py-2">
                        <small>
                            <FaExclamationTriangle className="me-1" />
                            Connection lost. Notifications may be delayed.
                        </small>
                    </Alert>
                )}
            </div>

            {/* Bulk Actions */}
            {selectedNotifications.size > 0 && (
                <div className="notification-bulk-actions">
                    <div className="d-flex justify-content-between align-items-center">
                        <small>{selectedNotifications.size} selected</small>
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={handleBulkDelete}
                            >
                                <FaTrash /> Delete
                            </Button>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setSelectedNotifications(new Set())}
                            >
                                Clear
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Notifications List */}
            <div className="notification-list">
                {displayedNotifications.length === 0 ? (
                    <div className="notification-empty">
                        <FaBell className="text-muted mb-2" size={24} />
                        <p className="text-muted mb-0">No notifications</p>
                    </div>
                ) : (
                    displayedNotifications.map((notification) => (
                        <div
                            key={notification.notification_id}
                            className={`notification-item ${isUnread(notification) ? 'unread' : 'read'}`}
                            onClick={() => handleNotificationClick(notification)}
                        >
                            <div className="notification-content">
                                <div className="notification-header">
                                    <div className="d-flex align-items-center">
                                        <input
                                            type="checkbox"
                                            className="me-2"
                                            checked={selectedNotifications.has(notification.notification_id)}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                toggleNotificationSelection(notification.notification_id);
                                            }}
                                        />
                                        {getNotificationIcon(notification.notification_type, notification.priority)}
                                        <div className="notification-title-section">
                                            <div className="notification-title">
                                                {notification.title}
                                            </div>
                                            <div className="notification-meta">
                                                {getPriorityBadge(notification.priority)}
                                                <span className="notification-time">
                                                    <FaClock className="me-1" />
                                                    {getTimeAgo(notification.created_at)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="notification-actions">
                                        {isUnread(notification) && (
                                            <Button
                                                variant="outline-success"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onMarkAsRead(notification.notification_id);
                                                }}
                                                title="Mark as read"
                                            >
                                                <FaCheck />
                                            </Button>
                                        )}
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDeleteNotification(notification.notification_id);
                                            }}
                                            title="Delete notification"
                                        >
                                            <FaTrash />
                                        </Button>
                                    </div>
                                </div>
                                
                                <div className="notification-message">
                                    {notification.message}
                                </div>

                                {notification.context_data && (
                                    <div className="notification-context">
                                        {notification.context_data.patient_name && (
                                            <small className="text-muted">
                                                Patient: {notification.context_data.patient_name}
                                            </small>
                                        )}
                                        {notification.context_data.form_title && (
                                            <small className="text-muted">
                                                Form: {notification.context_data.form_title}
                                            </small>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Footer */}
            {notifications.length > 10 && !showAllNotifications && (
                <div className="notification-panel-footer">
                    <Button
                        variant="link"
                        size="sm"
                        onClick={() => setShowAllNotifications(true)}
                        className="w-100"
                    >
                        Show all {notifications.length} notifications
                    </Button>
                </div>
            )}

            {showAllNotifications && notifications.length > 10 && (
                <div className="notification-panel-footer">
                    <Button
                        variant="link"
                        size="sm"
                        onClick={() => setShowAllNotifications(false)}
                        className="w-100"
                    >
                        Show fewer notifications
                    </Button>
                </div>
            )}
        </div>
    );
};

export default NotificationPanel;